import utils from "/node_modules/@ttcorestudio/viewer_data/library_files/Utils.js";
import plotMgrInstance from "/node_modules/@ttcorestudio/viewer_data/library_files/PlotMgr.js"
import * as d3 from "d3";
import {Plot} from "./Plot.js"

class ScatterPlot extends Plot{
    constructor(initParams) {
        super(initParams)
    }

    addScatterPlot(newData, keyForId, keyForX, typeForX, keyForY, typeForY, keyForR = undefined, minRadius = 6, maxRadius = 12, chartFormatting) {
        try {
            let scope = this;
            let formatting = { ...utils.defaultFormatting, ...(chartFormatting || {}) };
            scope.formatting = formatting
            let dataArr = [];
            scope.keys.x = keyForX;
            scope.keys.y = keyForY;
            scope.keys.r = keyForR;
            scope.typeForX = typeForX
            scope.typeForY = typeForY

            scope.canvas.innerHTML = "";
            let response = {};
            if (keyForR == undefined) {
                response = utils.filterScatterData(newData, keyForId, keyForX, keyForY, minRadius);
            }
            else {
                response = utils.filterBubbleData(newData, keyForId, keyForX, keyForY, keyForR, minRadius, maxRadius);
            }

            if (response.status == 'success') {
                dataArr = response.data;
            }

            dataArr.forEach(element => {
                scope.data[element.ids] = element;
                scope.elements[element.ids] = element.ids;
            });

            scope.dataArr = dataArr

            //Determine padding needed to prevent axis text from getting cut off
            const axisAngleRadians = utils.toRadians(scope.formatting.axisAngle);

            // Determine max Y characters based on type
            const maxYCharacters = typeForY === 'number' 
                ? utils.getMaxCharactersForNumber(dataArr.map(data => data.y))
                : utils.getMaxCharactersForString(dataArr.map(data => data.y));

            const paddingX = maxYCharacters * Math.abs(Math.cos(axisAngleRadians)) * 4;

            // Determine max X characters based on type
            const maxXCharacters = typeForX === 'number' 
                ? utils.getMaxCharactersForNumber(dataArr.map(data => data.y))
                : utils.getMaxCharactersForString(dataArr.map(data => data.y));

            const paddingY = maxXCharacters * Math.abs(Math.sin(axisAngleRadians)) * 5;

            scope.paddingY = paddingY;
            scope.paddingX = Math.max(paddingX, maxXCharacters * Math.abs(Math.cos(axisAngleRadians)));

            // set the dimensions and margins of the graph
            let width = scope.canvas.offsetWidth - scope.margin.left - scope.margin.right - scope.paddingX,
                height = scope.canvas.offsetHeight - scope.margin.top - scope.margin.bottom - scope.paddingY;

            let dimensions = "0 0 " + scope.canvas.offsetWidth + " " + scope.canvas.offsetHeight;

            //Determine max number of tick marks to use
            let maxTicksHorizontally = width/30
            let maxTicksVertically = height/30

            let items = {};
            let axisX = {};
            let axisY = {};
            let gridX = {};
            let gridY = {};

            // append the svg object to the body of the page
            scope.svg = d3.select(scope.canvas)
                .append('svg')
                .attr("viewBox", dimensions)
                .attr("preserveAspectRatio", "xMinYMin meet")
                .append('g')
                .attr('transform', 'translate(' + (scope.margin.left) + ',' + scope.margin.top + ')');

            scope.tooltip = d3.select(scope.canvas).append("div");
            utils.formatTooltipDefault(scope.tooltip, scope.defaultColor);

            // █▀▀ █▀▀ ▄▀▄ █   █▀▀ 
            // ▄██ █▄▄ █▀█ █▄▄ ██▄ 

            // Add X axis
            let x = {};
            if (typeForX === 'number') {
                x = d3.scaleLinear()
                    .domain([Math.min.apply(null, dataArr.map(d => d.x)), Math.max.apply(null, dataArr.map(d => d.x))])
                    .range([0, width])
                    .nice()
            }
            else {
                scope.xKeys = utils.uniqueValues(newData, keyForX).data;
                x = d3.scalePoint()
                    .range([0, width])
                    .domain(scope.xKeys)
            }

            let y = {};
            if (typeForY === 'number') {
                y = d3.scaleLinear()
                    .domain([Math.min.apply(null, dataArr.map(a => a.y)), Math.max.apply(null, dataArr.map(a => a.y))])
                    .range([height, 0])
                    .nice()
            }
            else {
                scope.yKeys = utils.uniqueValues(newData, keyForY).data;
                y = d3.scalePoint()
                    .range([height, 0])
                    .domain(scope.yKeys)
            }

            // ▄▀▄ ▀▄▀ ▀█▀ █▀▀ 
            // █▀█ █ █ ▄█▄ ▄██ 

            // Add X axis
            axisX = scope.svg.append("g")
                .attr('name', 'axisX')
                .attr("transform", "translate(" + scope.paddingX + "," + height + ")")
                .call(scope.typeForX === 'number' 
                    ? d3.axisBottom(x).ticks(maxTicksHorizontally) // Limit number of ticks for numeric scale
                    : d3.axisBottom(x).tickValues(
                        x.domain().filter((d, i) => i % Math.ceil(x.domain().length / maxTicksHorizontally) === 0)
                    ) // Limit ticks for categorical scale
                );
            
            // Add Y Axis
            axisY = scope.svg.append("g")
                .attr('name', 'axisY')
                .attr("transform", "translate(" + scope.paddingX + "," + 0 + ")")
                .call(scope.typeForY === 'number' 
                    ? d3.axisLeft(y).ticks(maxTicksVertically) // Limit number of ticks for numeric scale
                    : d3.axisLeft(y).tickValues(
                        y.domain().filter((d, i) => i % Math.ceil(y.domain().length / maxTicksVertically) === 0)
                    ) // Limit ticks for categorical scale
                );

            // █▀▀ █▀█ ▀█▀ █▀▄ 
            // █▄█ █▀▄ ▄█▄ █▄▀ 

            // Vertical Grid Lines (Grid X)
            gridX = scope.svg.append("g")
            .attr('name', 'gridX')
            .attr("transform", "translate(" + scope.paddingX + "," + height + ")")
            .call(scope.typeForX === 'number' 
                ? d3.axisBottom(x) // 
                    .ticks(maxTicksHorizontally)
                    .tickSize(-height) // 
                    .tickFormat("") 
                : d3.axisBottom(x)
                    .tickValues(
                        x.domain().filter((d, i) => i % Math.ceil(x.domain().length / maxTicksHorizontally) === 0)
                    )
                    .tickSize(-height)
                    .tickFormat("")
            );

            // Horizontal Grid Lines (Grid Y)
            gridY = scope.svg.append("g")
            .attr('name', 'gridY')
            .attr("transform", "translate(" + scope.paddingX + "," + 0 + ")")
            .call(scope.typeForY === 'number' 
                ? d3.axisLeft(y) 
                    .ticks(maxTicksVertically)
                    .tickSize(-width)
                    .tickFormat("") 
                : d3.axisLeft(y)
                    .tickValues(
                        y.domain().filter((d, i) => i % Math.ceil(y.domain().length / maxTicksVertically) === 0)
                    )
                    .tickSize(-width)
                    .tickFormat("")
            );

            // █▄▄ █▀█ █ █ █▀▀ █▄█ 
            // █▄█ █▀▄ █▄█ ▄██ █ █ 

            scope.svg
                .call(d3.brush() // Add the brush feature using the d3.brush function
                    .extent([[scope.paddingX, 0], [width + scope.paddingX, height]]) // initialise the brush area: start at 0,0 and finishes at width,height: it means I select the whole graph area
                    .on("start", (event) => {
                        clearChart(event); // Existing logic for start
                        checkMouseInBrushArea(event)
                    })
                    .on("brush", (event) => {
                        updateChart(event)
                    }) 
                )
            
            
            function checkMouseInBrushArea(event){
                const brushArea = event.selection;
                const [mouseX, mouseY] = d3.pointer(event);

                if (brushArea) {
                    const [[x0, y0], [x1, y1]] = brushArea;
                    //Mouse not in brush area
                    if (mouseX === x0 && mouseX === x1 && mouseY === y0 && mouseY === y1) {
                        emitClearSelection();
                    }
                }
            }

            function emitClearSelection() {
                const brushStartEvent = new CustomEvent('emitClearSelection')
                document.dispatchEvent(brushStartEvent);
            }

            // Function that is triggered when brushing is started
            function clearChart(event) {
                scope.clearAllPickSelection()
            }

            // Function that is triggered when brushing is performed
            function updateChart(event) {
                const extent = event.selection;
                let selectedIds = [];
                items.classed(scope.selectionColor, (d) => {
                    let brushed = isBrushed(extent, x(d.x), y(d.y))
                    if (brushed) {
                        selectedIds.push(d.ids);
                    }
                    return brushed;
                })

                scope.onScrub(selectedIds);
            }

            // A function that return TRUE or FALSE according if a dot is in the selection or not
            function isBrushed(brush_coords, cx, cy) {
                let x0 = brush_coords[0][0] - scope.paddingX,
                    x1 = brush_coords[1][0] - scope.paddingX,
                    y0 = brush_coords[0][1],
                    y1 = brush_coords[1][1];
                return x0 <= cx && cx <= x1 && y0 <= cy && cy <= y1;    // This return TRUE or FALSE depending on if the points is in the selected area
            }

            // ▀█▀ ▀█▀ █▀▀ █▄ ▄█ █▀▀ 
            // ▄█▄  █  ██▄ █ ▀ █ ▄██ 

            // Add dots
            items = scope.svg
                .selectAll("dot")
                .data(dataArr)
                .join("circle")
                .attr('transform', "translate(" + scope.paddingX + "," + 0 + ")")
                .attr("cx", function (d) { return x(d.x); })
                .attr("cy", function (d) { return y(d.y); })
                .attr("r", function (d) { return d.r; })
                .attr("data-id", function (d, i) {
                    scope.groups[d.ids] = this;
                    return d.ids;
                });

            // █▀▀ █▀█ █▀█ █▄ ▄█ ▄▀▄ ▀█▀ ▀█▀ ▀█▀ █▄ █ █▀▀ 
            // █▀  █▄█ █▀▄ █ ▀ █ █▀█  █   █  ▄█▄ █ ▀█ █▄█ 
            utils.formatFillItem(items, scope.tooltip, scope.defaultColor, scope.defaultOpacity);
            utils.formatAxis(axisX, formatting.xAxis, scope.axisColor,1.0,scope.formatting.axisAngle);
            utils.formatAxis(axisY, formatting.yAxis, scope.axisColor,1.0,scope.formatting.axisAngle);
            utils.formatGrid(gridX, formatting.xGrid, scope.gridColor, scope.gridOpacity);
            utils.formatGrid(gridY, formatting.yGrid, scope.gridColor, scope.gridOpacity);


            const returnValue = {
                'status': 'success',
                'message': 'Scatter plot added'
            }
            scope.plotAdded = true
            return returnValue;
        }
        catch (err) {
            return {
                'status': 'failed',
                'message': 'Failed to add Scatter plot.' + err.message
            }
        }
    }

    resizeChart(){
        let scope = this

        let width = scope.canvas.offsetWidth - scope.margin.left - scope.margin.right - scope.paddingX
        let height = scope.canvas.offsetHeight - scope.margin.top - scope.margin.bottom - scope.paddingY -20

        //Determine max number of tick marks to use
        let maxTicksHorizontally = width/30
        let maxTicksVertically = height/30

        // Update SVG dimensions
        d3.select(scope.canvas).select("svg")
            .attr("viewBox", `0 0 ${scope.canvas.offsetWidth} ${scope.canvas.offsetHeight}`)

        // Update X Axis
        let x = {}
        if (scope.typeForX === 'number'){
            x = d3.scaleLinear()
                .domain([Math.min.apply(null, scope.dataArr.map(d => d.x)), Math.max.apply(null, scope.dataArr.map(d => d.x))])
                .range([0, width])
                .nice()
        } else {
            x = d3.scalePoint()
                .range([0, width])
                .domain(scope.xKeys);
        }

        //Update Y Axis
        let y = {}
        if (scope.typeForY === 'number') {
            y = d3.scaleLinear()
                .domain([Math.min.apply(null, scope.dataArr.map(a => a.y)), Math.max.apply(null, scope.dataArr.map(a => a.y))])
                .range([height, 0])
                .nice()
        }
        else {
            y = d3.scalePoint()
                .range([height, 0])
                .domain(scope.yKeys);
        }

        // Update axes
        scope.svg.selectAll('g[name="axisX"]')
        .attr('transform', "translate(" + scope.paddingX + "," + height + ")")
        .call(scope.typeForX === 'number' 
            ? d3.axisBottom(x).ticks(maxTicksHorizontally) // Limit number of ticks for numeric scale
            : d3.axisBottom(x).tickValues(
                x.domain().filter((d, i) => i % Math.ceil(x.domain().length / maxTicksHorizontally) === 0)
            ) // Limit ticks for categorical scale
        );

        scope.svg.selectAll('g[name="axisY"]')
        .attr('transform', "translate(" + scope.paddingX + "," + 0 + ")")
        .call(scope.typeForY === 'number' 
            ? d3.axisLeft(y).ticks(maxTicksVertically) // Limit number of ticks for numeric scale
            : d3.axisLeft(y).tickValues(
                y.domain().filter((d, i) => i % Math.ceil(y.domain().length / maxTicksVertically) === 0)
            ) // Limit ticks for categorical scale
        );

        //Update Grid
        scope.svg.selectAll('g[name="gridX"]')
        .attr('transform', "translate(" + scope.paddingX + "," + height + ")")
            .call(scope.typeForX === 'number' 
                ? d3.axisBottom(x) // 
                    .ticks(maxTicksHorizontally)
                    .tickSize(-height) // 
                    .tickFormat("") 
                : d3.axisBottom(x)
                    .tickValues(
                        x.domain().filter((d, i) => i % Math.ceil(x.domain().length / maxTicksHorizontally) === 0)
                    )
                    .tickSize(-height)
                    .tickFormat("")
            );

        scope.svg.selectAll('g[name="gridY"]')
        .attr('transform', "translate(" + scope.paddingX + "," + 0 + ")")
        .call(scope.typeForY === 'number' 
            ? d3.axisLeft(y) 
                .ticks(maxTicksVertically)
                .tickSize(-width)
                .tickFormat("") 
            : d3.axisLeft(y)
                .tickValues(
                    y.domain().filter((d, i) => i % Math.ceil(y.domain().length / maxTicksVertically) === 0)
                )
                .tickSize(-width)
                .tickFormat("")
        );

        //Update Items
        let items = scope.svg.selectAll("circle")
            .attr("cx", function (d) { return x(d.x); })
            .attr("cy", function (d) { return y(d.y); })
            .attr("r", function (d) { return d.r; })
            scope.svg
            .call(d3.brush()                 // Add the brush feature using the d3.brush function
                .extent([[0, 0], [width, height]]) // initialise the brush area: start at 0,0 and finishes at width,height: it means I select the whole graph area
                .on("start", (event) => {
                    clearChart(event); // Existing logic for start
                    checkMouseInBrushArea(event)
                })
                .on("brush", updateChart) // Each time the brush selection changes, trigger the 'updateChart' function
            )

        // Function that is triggered when brushing is started
        function clearChart(event) {
            scope.clearAllPickSelection()
        }

        function checkMouseInBrushArea(event){
            const brushArea = event.selection;
            const [mouseX, mouseY] = d3.pointer(event);

            if (brushArea) {
                const [[x0, y0], [x1, y1]] = brushArea;
                //Mouse not in brush area
                if (mouseX === x0 && mouseX === x1 && mouseY === y0 && mouseY === y1) {
                    emitClearSelection();
                }
            }
        }

        scope.svg.call(d3.brush()
            .extent([[scope.paddingX, 0], [width + scope.paddingX, height]])
            .on("start", clearChart)
            .on("brush", updateChart)
        );

        function emitClearSelection() {
            const brushStartEvent = new CustomEvent('emitClearSelection')
            document.dispatchEvent(brushStartEvent);
        }

        // Function that is triggered when brushing is performed
        function updateChart(event) {
            const extent = event.selection;
            let selectedIds = [];
            items.classed(scope.selectionColor, (d) => {
                let brushed = isBrushed(extent, x(d.x), y(d.y))
                if (brushed) {
                    selectedIds.push(d.ids);
                }
                return brushed;
            })

            scope.onScrub(selectedIds);
        }

        // A function that return TRUE or FALSE according if a dot is in the selection or not
        function isBrushed(brush_coords, cx, cy) {
            let x0 = brush_coords[0][0] - scope.paddingX,
                x1 = brush_coords[1][0] - scope.paddingX,
                y0 = brush_coords[0][1],
                y1 = brush_coords[1][1];
            return x0 <= cx && cx <= x1 && y0 <= cy && cy <= y1;    // This return TRUE or FALSE depending on if the points is in the selected area
        }

        // Optionally format axes, grids, and bars
        utils.formatAxis(d3.select(scope.canvas).select("[name='axisX']"), scope.formatting.xAxis, scope.axisColor, 1.0, scope.formatting.axisAngle);
        utils.formatAxis(d3.select(scope.canvas).select("[name='axisY']"), scope.formatting.yAxis, scope.axisColor, 1.0, scope.formatting.axisAngle);
        utils.formatGrid(d3.select(scope.canvas).select("[name='gridX']"), scope.formatting.xGrid, scope.gridColor, scope.gridOpacity);
        utils.formatGrid(d3.select(scope.canvas).select("[name='gridY']"), scope.formatting.yGrid, scope.gridColor, scope.gridOpacity);
    }

    //  ████  ███  █      ███  ████     ████  █   █ 
    // █     █   █ █     █   █ █   █    █   █  █ █  
    // █     █   █ █     █   █ ████     ████    █   
    // █     █   █ █     █   █ █   █    █   █   █   
    //  ████  ███  █████  ███  █   █    ████    █   


    colorElements(ids, colors) {
        let scope = this;
        
        Object.keys(scope.groups).forEach(key => {
            let index = ids.indexOf(key);
            let highlightedElement = d3.select(scope.groups[key]);
            scope.applyColor(highlightedElement, colors[index])
        });
    }


    // █████ █████ █     █████  ████ █████ 
    // █     █     █     █     █       █   
    // █████ ████  █     ████  █       █   
    //     █ █     █     █     █       █   
    // █████ █████ █████ █████  ████   █   



    // SELECTION event
    pickElement(event, callBack) {
        let scope = this;
        
        scope.clearSelection();
        let group = event.target;
        let selectedElement;
        if (selectedElement = d3.select(group)) {
            if (scope.data[selectedElement.attr('data-id')]) {
                scope.selectedElements.push(scope.data[selectedElement.attr('data-id')].ids)
                scope.applySelectionColor(selectedElement);
                callBack([...new Set(scope.selectedElements)]);
            }
            else {
                callBack('');
                plotMgrInstance.deSelectElementsInAllPlots();
            }
        }
    }

    selectElements(elementIDs) {
        let scope = this;
        
        if (scope.svg != undefined || scope.svg != null) {
            if (elementIDs) {

                elementIDs.forEach(key => {
                    var element = d3.select(scope.groups[key]);
                    scope.applySelectionColor(element);
                });
            }
        }
    }

    // Clear pick selection
    clearSelection() {
        let scope = this;
        
        if (!scope.multipleSelectionFlagCheck) {
            for (let i = 0; i < scope.temp_selection.length; i++) {
                let element = scope.temp_selection[i];

            if (element._groups[0][0]) {
                element.attr('data-isSel', false);
                element.attr('data-isPar', false);

                var color = scope.defaultColor;
                if (element.attr('data-isClr') == 'true') color = element.attr('data-color');

                element.style("fill", color);
                element.style('opacity', scope.defaultOpacity);
            }
            }
            
            scope.temp_selection = [];
            scope.selectedElements = [];
        }
    }


    // █   █ █████  ████ █   █ █     █████  ████ █   █ █████ 
    // █   █   █   █     █   █ █       █   █     █   █   █   
    // █████   █   █  ██ █████ █       █   █  ██ █████   █   
    // █   █   █   █   █ █   █ █       █   █   █ █   █   █   
    // █   █ █████  ███  █   █ █████ █████  ███  █   █   █   


    // Highlight event
    highlightGroup(highlightedElement, event, callBack,){
        let scope = this;

        if (highlightedElement.attr('data-id') != undefined) {
            scope.applyHighlightColor(highlightedElement);
            scope.applyToolTip(event, highlightedElement.attr('data-id'));
            callBack([scope.data[highlightedElement.attr("data-id")].ids]);
        }
        else {
            callBack("");
            plotMgrInstance.deHighlightElementsInAllPlots();
        }
    }

    highlightElements(elementIDs) {
        let scope = this;
        
        if (scope.highlightElementFlagCheck) {
            if (scope.svg != undefined) {
                if (elementIDs) {

                    elementIDs.forEach(key => {
                        var element = d3.select(scope.groups[key]);
                        scope.applyHighlightColor(element);
                    });
                }
            }
        }
    }

    //Apply ToolTip
    buildTooltipContent(id) {
        let scope = this;
        let content = "<strong>" + scope.keys.x + "</strong> | " + utils.setDigits(scope.data[id].x, scope.digits) + "<br/>";
        content += "<strong>" + scope.keys.y + "</strong> | " + utils.setDigits(scope.data[id].y, scope.digits) + "<br/>";
        return content;
    }

    // Clear highlight selection 
    clearHighlights() {
        let scope = this;
        
        for (let i = 0; i < scope.temp_highlight.length; i++) {
            let element = scope.temp_highlight[i];

            if (element._groups[0][0]) {
                var color = scope.defaultColor;
                var opacity = scope.defaultOpacity;
                if (element.attr('data-isClr') == 'true') color = element.attr('data-color');
                if (element.attr('data-isSel') == 'true') color = scope.selectionColor;
                if (element.attr('data-isPar') == 'true') {
                    color = scope.selectionColor;
                    opacity = scope.selectionOpacity;
                }

                element.style("fill", color);
                element.style('opacity', opacity);
            }
        }
        scope.temp_highlight = [];
    }
}
export { ScatterPlot }
